import { FULL_ROUND, HALF_ROUND } from "./handicap.js";
import type { Round } from "./types.js";
import { getFormat } from "./formats/index.js";

/**
 * Team pairing generation for 2-player teams (four-ball, scramble, shamble,
 * alternate shot). Uses the standard round-robin "circle method": for N players
 * it produces N-1 rounds, each a perfect matching into N/2 pairs, such that every
 * possible pair of players occurs in exactly one round. For N=4 that's exactly the
 * three possible pairings — three 6-hole segments covers all of them (spec §11).
 */
export function circleMethodRounds<T>(players: T[]): [T, T][][] {
  const n = players.length;
  if (n < 2 || n % 2 !== 0) {
    throw new Error("circle method requires an even number of players (2+)");
  }

  const rounds: [T, T][][] = [];
  const rotating = players.slice(1);
  const fixed = players[0]!;

  for (let r = 0; r < n - 1; r++) {
    const circle = [fixed, ...rotating];
    const round: [T, T][] = [];
    for (let i = 0; i < n / 2; i++) {
      round.push([circle[i]!, circle[n - 1 - i]!]);
    }
    rounds.push(round);
    rotating.unshift(rotating.pop()!);
  }

  return rounds;
}

const BYE = " bye";

/**
 * Generates `numSegments` rounds of team assignments, distributing partnerships as
 * evenly as possible. If numSegments exceeds the number of distinct pairings (N-1),
 * the schedule cycles rather than repeating a pairing early.
 *
 * Odd player counts are handled rather than rejected — five people turning up is
 * ordinary, and this used to throw straight out of the button's click handler. The
 * circle method needs an even field, so it runs on N+1 with a bye; the player drawn
 * against the bye joins the smallest side, making one threesome that moves around the
 * group segment by segment instead of the same person always playing three-handed.
 */
export function generateTeamRotation(playerIds: string[], numSegments: number): string[][][] {
  if (playerIds.length < 2 || numSegments < 1) return [];

  const odd = playerIds.length % 2 !== 0;
  const rounds = circleMethodRounds(odd ? [...playerIds, BYE] : playerIds);

  const result: string[][][] = [];
  for (let i = 0; i < numSegments; i++) {
    const round = rounds[i % rounds.length]!;
    const teams: string[][] = [];
    let spare: string | null = null;

    for (const [a, b] of round) {
      if (a === BYE) spare = b;
      else if (b === BYE) spare = a;
      else teams.push([a, b]);
    }

    if (spare !== null) {
      // Folding the spare into the smallest side makes a threesome that moves around the
      // group. With three players there is only one real pair, so folding leaves a single
      // side — and a segment with one side is not a match, so the round scores nothing at
      // all. Below two sides, the spare plays on its own instead: a threesome is 2 v 1,
      // and the rotation still moves who goes solo from segment to segment.
      const smallest = teams.reduce<string[] | null>(
        (min, t) => (min === null || t.length < min.length ? t : min),
        null,
      );
      if (smallest && teams.length >= 2) smallest.push(spare);
      else teams.push([spare]);
    }
    result.push(teams);
  }
  return result;
}

/**
 * Segments of one-player sides for singles match play, ordered so that consecutive sides
 * are the opponents: `[[a], [b], [c], [d]]` means a plays b and c plays d.
 *
 * The circle method already produces exactly this — its output is a list of *opponent*
 * pairs, which `generateTeamRotation` happens to read as partnerships. Here each pair is
 * simply flattened into two sides, so everyone plays everyone once before any repeat.
 *
 * An odd field gets a bye each segment, rotating through the group, since there is
 * nobody for the last player to play.
 */
export function generateSinglesRotation(playerIds: string[], numSegments: number): string[][][] {
  if (playerIds.length < 2 || numSegments < 1) return [];

  const odd = playerIds.length % 2 !== 0;
  const rounds = circleMethodRounds(odd ? [...playerIds, BYE] : playerIds);

  const result: string[][][] = [];
  for (let i = 0; i < numSegments; i++) {
    const round = rounds[i % rounds.length]!;
    const sides: string[][] = [];
    for (const [a, b] of round) {
      if (a === BYE || b === BYE) continue; // the odd player sits this segment out
      sides.push([a], [b]);
    }
    result.push(sides);
  }
  return result;
}

export interface SegmentProblem {
  /** `error` breaks scoring outright; `warning` scores, but probably not as intended. */
  level: "error" | "warning";
  message: string;
}

/**
 * Checks the invariants the scoring engine assumes about segments but has never
 * enforced. Nothing validated these while they could only come from the generator;
 * hand-editing makes every one of them reachable.
 *
 * Pure so it can be tested directly — the setup screen only renders what it returns.
 */
export interface ValidateSegmentsOptions {
  nameOf?: (id: string) => string;
  /**
   * True for a team-scope format, where sides are what the scoring is built out of.
   * Without it an empty segment list is just an individual round, which is fine.
   */
  requiresSides?: boolean;
}

export function validateSegments(
  playerIds: string[],
  segments: { startHole: number; endHole: number; teams: string[][] }[],
  holeCount: number,
  opts: ValidateSegmentsOptions = {},
): SegmentProblem[] {
  const nameOf = opts.nameOf ?? ((id: string) => id);
  const problems: SegmentProblem[] = [];

  if (segments.length === 0) {
    // The one configuration guaranteed to pay nobody used to be the only one this said
    // nothing about: a team format scores by segment, so with no segments the scoring
    // loop has nothing to iterate and every player finishes on zero.
    if (opts.requiresSides) {
      problems.push({
        level: "error",
        message:
          "This format scores by sides, and no segments are set — the round would score nothing. Add a segment, or use Auto-generate.",
      });
    }
    return problems;
  }

  const list = (ids: string[]) => ids.map(nameOf).join(", ");

  segments.forEach((seg, i) => {
    const label = `Segment ${i + 1} (holes ${seg.startHole}-${seg.endHole})`;
    const sides = seg.teams.filter((t) => t.length > 0);

    if (sides.length < 2) {
      problems.push({
        level: "error",
        message: `${label} has ${sides.length === 0 ? "no sides" : "only one side"} — a segment is a match, so it needs at least two. It will score nothing.`,
      });
    }
    if (seg.teams.some((t) => t.length === 0)) {
      problems.push({ level: "warning", message: `${label} has an empty side, which is ignored.` });
    }

    // A player on both sides breaks single-ball formats outright: the team's score is
    // stored against its first member, so the two sides would share one entry.
    const seen = new Set<string>();
    const doubled = new Set<string>();
    for (const team of seg.teams) {
      for (const id of team) {
        if (seen.has(id)) doubled.add(id);
        seen.add(id);
      }
    }
    if (doubled.size > 0) {
      problems.push({
        level: "error",
        message: `${label}: ${list([...doubled])} ${doubled.size === 1 ? "is" : "are"} on more than one side.`,
      });
    }

    const missing = playerIds.filter((id) => !seen.has(id));
    if (missing.length > 0) {
      problems.push({
        level: "warning",
        message: `${label}: ${list(missing)} ${missing.length === 1 ? "isn't" : "aren't"} on a side and will score nothing for these holes.`,
      });
    }

    if (seg.startHole < 1 || seg.endHole > holeCount || seg.startHole > seg.endHole) {
      problems.push({
        level: "error",
        message: `${label} is outside the round's ${holeCount} holes.`,
      });
    }
  });

  // Coverage: every hole in the round should belong to exactly one segment.
  const owners = new Map<number, number>();
  segments.forEach((seg, i) => {
    for (let h = Math.max(1, seg.startHole); h <= Math.min(holeCount, seg.endHole); h++) {
      if (!owners.has(h)) owners.set(h, i);
      else
        problems.push({
          level: "error",
          message: `Hole ${h} is in both segment ${owners.get(h)! + 1} and segment ${i + 1}; only the first counts.`,
        });
    }
  });
  const gaps: number[] = [];
  for (let h = 1; h <= holeCount; h++) if (!owners.has(h)) gaps.push(h);
  if (gaps.length > 0) {
    problems.push({
      level: "warning",
      message: `No side is set for hole${gaps.length === 1 ? "" : "s"} ${describeRun(gaps)} — those holes score nothing.`,
    });
  }

  return problems;
}

/** "1, 2, 3, 7" as "1-3, 7", so a gap message stays readable on a phone. */
function describeRun(holes: number[]): string {
  const parts: string[] = [];
  let start = holes[0]!;
  let prev = start;
  for (const h of holes.slice(1)) {
    if (h === prev + 1) prev = h;
    else {
      parts.push(start === prev ? `${start}` : `${start}-${prev}`);
      start = h;
      prev = h;
    }
  }
  parts.push(start === prev ? `${start}` : `${start}-${prev}`);
  return parts.join(", ");
}

export function pairKey(a: string, b: string): string {
  return [a, b].sort().join("|");
}

/** How many times each possible pair of players has actually been partnered. */
export function computeCoverageMatrix(
  playerIds: string[],
  segments: { teams: string[][] }[],
): Map<string, number> {
  const counts = new Map<string, number>();
  for (let i = 0; i < playerIds.length; i++) {
    for (let j = i + 1; j < playerIds.length; j++) {
      counts.set(pairKey(playerIds[i]!, playerIds[j]!), 0);
    }
  }
  for (const segment of segments) {
    for (const team of segment.teams) {
      for (let i = 0; i < team.length; i++) {
        for (let j = i + 1; j < team.length; j++) {
          const key = pairKey(team[i]!, team[j]!);
          counts.set(key, (counts.get(key) ?? 0) + 1);
        }
      }
    }
  }
  return counts;
}

/**
 * Where an added segment's holes come from, given the segments a round already has.
 *
 * "Start after the last one and run to the end of the round" is the obvious rule and it
 * is wrong most of the time, because there is usually no end left: both `evenHoleRanges`
 * and this function leave the final segment sitting on the last hole. Clamping the new
 * start back to `holeCount` — what the setup screen used to do — produces a segment
 * overlapping the previous one, and an overlap is an error-level problem, so the round
 * then refuses to save with nothing on the button that caused it to say why.
 *
 * So: take the tail of the round when there is one, and otherwise halve a segment that
 * still has holes to give. The *widest* one, ties going to the last — halving only the
 * last segment gives out on a fully covered eighteen after five presses, with a
 * one-hole tail and six-hole segments in front of it that were never asked.
 *
 * Returns null when every hole is already a segment of its own and there is genuinely
 * nothing left to divide.
 */
export function segmentsWithOneMore(
  segments: Segmentish[],
  holeCount: number,
): Segmentish[] | null {
  const covered = segments.reduce((max, s) => Math.max(max, s.endHole), 0);
  if (covered < holeCount) {
    return [...segments, { startHole: covered + 1, endHole: holeCount, teams: [[], []] }];
  }

  let splitIdx = -1;
  let widest = 0;
  segments.forEach((s, i) => {
    const holes = s.endHole - s.startHole + 1;
    if (holes >= 2 && holes >= widest) {
      widest = holes;
      splitIdx = i;
    }
  });
  const target = segments[splitIdx];
  if (!target) return null;

  const split = Math.floor((target.startHole + target.endHole) / 2);
  return [
    ...segments.slice(0, splitIdx),
    { ...target, endHole: split },
    { startHole: split + 1, endHole: target.endHole, teams: [[], []] },
    ...segments.slice(splitIdx + 1),
  ];
}

/**
 * What has to change about a round when the card underneath it turns out to be a nine.
 *
 * A round carries `holeSet: "front9"` quite happily until the course it points at is a
 * nine-hole one, at which point the front nine and the whole card are the same nine and
 * the distinction is nonsense — worse, a stored `"back9"` would have the round looking
 * for holes 10-18 on a card that stops at 9. So picking a nine-hole course settles the
 * question rather than leaving it: back to "all of them", and the segment ranges
 * re-cut to fit, exactly as changing the hole set by hand would do.
 *
 * Returns an empty patch when there is nothing to settle, so it can be spread into any
 * `onChange` without deciding whether it applies.
 */
export function narrowToCard(cardHoles: number | undefined, round: Round): Partial<Round> {
  if (cardHoles === undefined || cardHoles > HALF_ROUND) return {};
  if (round.holeSet === "all18" && round.segments.length === 0) return {};

  const ranges = evenHoleRanges(cardHoles, round.segments.length || 1);
  return {
    holeSet: "all18",
    ...(round.segments.length > 0
      ? {
          segments: round.segments.map((seg, i) => ({
            ...seg,
            startHole: ranges[i]?.startHole ?? seg.startHole,
            endHole: ranges[i]?.endHole ?? seg.endHole,
          })),
        }
      : {}),
  };
}

/** Divides a hole range as evenly as possible across N segments (e.g. 18 holes / 3 -> three 6s). */
export function evenHoleRanges(
  totalHoles: number,
  numSegments: number,
): { startHole: number; endHole: number }[] {
  const ranges: { startHole: number; endHole: number }[] = [];
  const base = Math.floor(totalHoles / numSegments);
  let extra = totalHoles - base * numSegments;
  let start = 1;
  for (let i = 0; i < numSegments; i++) {
    const size = base + (extra > 0 ? 1 : 0);
    if (extra > 0) extra--;
    ranges.push({ startHole: start, endHole: start + size - 1 });
    start += size;
  }
  return ranges;
}

/**
 * Which holes' scores stop meaning anything when a round's team assignments change.
 *
 * Only single-ball formats are affected. A scramble or alternate-shot side plays one
 * ball, and that one score is recorded against a player — so moving that player moves
 * the score onto whichever side they land on, while the side they left reads as never
 * having played. The round then quietly scores zero. Formats where everyone holes out
 * their own ball just recompute from the same cards, so their scores stay valid and
 * must not be touched.
 *
 * Compares each hole's side membership before and after: a hole is invalidated when the
 * set of sides playing it changed at all — including gaining or losing a segment.
 * Switching the format itself between a single-ball (perTeam) and an individual-ball
 * (perPlayer) entry mode invalidates every hole in the round outright, even if the
 * segments/teams are byte-for-byte unchanged — the *same* stored score row means
 * something different under each mode (one side's shared ball vs. one player's own),
 * so the segment-membership comparison below (which only looks at `segments`) would
 * otherwise see no change and wrongly leave the old rows in place.
 *
 * Returns absolute hole numbers, so a back nine comes back as 10-18.
 */
export function holesInvalidatedByTeamChange(
  before: { segments: Segmentish[]; holeSet: HoleSetish; formatId: string } | null,
  after: { segments: Segmentish[]; holeSet: HoleSetish; formatId: string },
  /**
   * How many holes the round actually plays, when the caller knows — which it does
   * whenever the course is to hand. Nine-hole courses are why this is a parameter:
   * `holeSet` says "all of them" on a nine-hole card exactly as it does on an
   * eighteen, so without the card there is no way to tell eighteen positions from
   * nine. Left out, this assumes eighteen and over-invalidates, which costs a few
   * scores that were never there rather than leaving stale ones that were.
   */
  cardHoleCount?: number,
): number[] {
  if (!before) return [];

  const holeCount =
    after.holeSet === "all18" ? (cardHoleCount ?? FULL_ROUND) : HALF_ROUND;
  const offset = after.holeSet === "back9" ? HALF_ROUND : 0;

  const beforeIsTeam = getFormat(before.formatId).entry === "perTeam";
  const afterIsTeam = getFormat(after.formatId).entry === "perTeam";

  if (beforeIsTeam !== afterIsTeam) {
    return Array.from({ length: holeCount }, (_, i) => i + 1 + offset);
  }
  if (!afterIsTeam) return [];

  const invalidated: number[] = [];
  for (let position = 1; position <= holeCount; position++) {
    if (sidesAt(before, position) !== sidesAt(after, position)) invalidated.push(position + offset);
  }
  return invalidated;
}

interface Segmentish {
  startHole: number;
  endHole: number;
  teams: string[][];
}
type HoleSetish = "all18" | "front9" | "back9";

/** A comparable fingerprint of who is playing together at one position. Order within a
 * side and between sides is irrelevant — reordering isn't a team change. */
function sidesAt(round: { segments: Segmentish[] }, position: number): string {
  const segment = round.segments.find((s) => position >= s.startHole && position <= s.endHole);
  if (!segment) return "";
  return segment.teams
    .filter((t) => t.length > 0)
    .map((t) => [...t].sort().join(","))
    .sort()
    .join("|");
}
